/**
 * Modal to choose which container to add a product to when adding from the store.
 * Shows matching mix blocks (same origin/port/supplier) or "Añadir a nuevo contenedor".
 * One instance per "add" action (shared code, used from StoreProduct and StoreFamily).
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatContainerType } from '@/lib/container-type';
import { getMatchingMixBlockIndices } from '@/pages/store/store-cart-sections';
import type { QuotationDocBlocks, MixBlock, SourcingItem } from '@/lib/store-types';
import type { QuotationCartLine } from '@/hooks/use-quotation-cart';
import { Layers, Package, Container } from 'lucide-react';

export interface AddToQuotationContainerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  line: QuotationCartLine;
  item: SourcingItem;
  blocks: QuotationDocBlocks | undefined;
  add: (line: QuotationCartLine, options?: { mixBlockIndex?: number }) => Promise<void>;
  onSuccess: () => void;
  onError?: (message: string) => void;
}

/** Label for a mix block in the choice list. */
function mixBlockLabel(block: MixBlock, index: number): string {
  const containerLabel = block.container_type ? formatContainerType(block.container_type) : 'contenedor';
  const count = block.rows?.length ?? 0;
  return `Bloque mixto ${index + 1} · ${containerLabel} · ${count} producto(s)`;
}

export function AddToQuotationContainerModal({
  open,
  onOpenChange,
  line,
  item,
  blocks,
  add,
  onSuccess,
  onError,
}: AddToQuotationContainerModalProps) {
  const [adding, setAdding] = useState(false);
  const matchingIndices = getMatchingMixBlockIndices(blocks, item);
  const mixBlocks = blocks?.mix ?? [];
  const hasMatching = matchingIndices.length > 0;

  const handleChoose = async (mixBlockIndex: number | null) => {
    setAdding(true);
    try {
      if (mixBlockIndex !== null) {
        await add(line, { mixBlockIndex });
      } else {
        await add(line);
      }
      onOpenChange(false);
      onSuccess();
    } catch {
      onError?.('No se pudo añadir a la cotización');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md min-w-0 max-w-[calc(100vw-2rem)]">
        <DialogHeader className="min-w-0">
          <DialogTitle>¿Dónde añadir este producto?</DialogTitle>
          <DialogDescription className="break-words min-w-0">
            {hasMatching
              ? 'Elige un contenedor con el mismo origen, puerto y proveedor, o crea uno nuevo.'
              : 'No hay contenedores que coincidan con el origen/puerto/proveedor de este producto. Se creará un nuevo contenedor.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2 min-w-0">
          {matchingIndices.map((idx) => {
            const block = mixBlocks[idx] as MixBlock | undefined;
            if (!block) return null;
            return (
              <Button
                key={idx}
                variant="outline"
                className="w-full justify-start gap-2 h-auto py-3 text-left border-sky-200 hover:bg-sky-50 hover:border-sky-300 min-w-0"
                onClick={() => handleChoose(idx)}
                disabled={adding}
              >
                <Layers className="h-4 w-4 shrink-0 text-sky-600" />
                <span className="min-w-0 break-words">{mixBlockLabel(block, idx)}</span>
              </Button>
            );
          })}
          <Button
            variant="outline"
            className="w-full justify-start gap-2 h-auto py-3 text-left border-dashed border-sky-300 bg-sky-50/50 hover:bg-sky-100/50 min-w-0 flex-wrap"
            onClick={() => handleChoose(null)}
            disabled={adding}
          >
            {hasMatching ? (
              <>
                <Package className="h-4 w-4 shrink-0 text-sky-600" />
                <span className="min-w-0 break-words">Añadir a nuevo contenedor</span>
              </>
            ) : (
              <>
                <Container className="h-4 w-4 shrink-0 text-sky-600" />
                <span className="min-w-0 break-words">Añadir a nuevo contenedor</span>
                <span className="text-xs text-zinc-500 min-w-0 break-words">(no hay contenedores que coincidan)</span>
              </>
            )}
          </Button>
        </div>
        <DialogFooter className="sm:justify-start">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={adding}>
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
